import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Edit3, X, Plus, Trash2, Sparkles, CheckCircle2, GitBranch, Printer, Eye } from "lucide-react";
import { toast } from "sonner";
import { EligibleStatusPill, ResultSourcePill } from "@/components/StatusPill";
import ClientSummaryPreview from "@/components/client/ClientSummaryPreview";

const LOAN_TYPES = ["Direct Subsidized", "Direct Unsubsidized", "Direct Consolidated Subsidized", "Direct Consolidated Unsubsidized", "FFEL", "Parent PLUS", "Grad PLUS", "Perkins", "Private", "Other", "Unknown"];
const RATE_TYPES = ["fixed", "variable", "any", "unknown"];
const LOAN_STATUSES = ["active", "paid", "default", "deferment", "forbearance", "consolidated", "cancelled", "unknown"];
const FILING = [
    { v: "single", l: "Single" },
    { v: "married_filing_jointly", l: "Married Filing Jointly" },
    { v: "married_filing_separately", l: "Married Filing Separately" },
    { v: "head_of_household", l: "Head of Household" },
    { v: "unknown", l: "Unknown" },
];
const YNU = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "unknown", l: "Unknown" }];
const SERVICE_TYPES = [
    { v: "needs_review", l: "Needs Review" },
    { v: "consolidation_standard_graduated", l: "Consolidation into Standard/Graduated" },
    { v: "consolidation_idr", l: "Consolidation into IDR" },
    { v: "enter_idr_plan", l: "Enter IDR Plan" },
    { v: "program_switch", l: "Program Switch" },
    { v: "renewal", l: "Renewal" },
    { v: "change_of_circumstance", l: "Change of Circumstance" },
    { v: "other", l: "Other" },
];
const PLAN_OPTIONS = ["Standard Repayment", "Extended Standard Repayment", "Income-Based Repayment", "Pay As You Earn", "Income-Contingent Repayment", "Repayment Assistance Plan", "Other / Manual"];
const IBR_VARIANTS = [
    { v: "auto", l: "Auto-detect" },
    { v: "new_ibr_10_percent", l: "New IBR (10%)" },
    { v: "old_ibr_15_percent", l: "Old IBR (15%)" },
    { v: "unknown", l: "Unknown" },
];
const ELIGIBLE = [{ v: "eligible", l: "Eligible" }, { v: "not_eligible", l: "Not Eligible" }, { v: "needs_review", l: "Needs Review" }, { v: "manual", l: "Manual / Estimated" }];

const fmt$ = (n) => n == null || n === "" ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => n == null || n === "" ? "—" : `${Number(n).toFixed(2)}%`;
const num = (v) => v === "" || v === null || v === undefined ? null : Number(v);
const int = (v) => v === "" || v === null || v === undefined ? null : parseInt(v, 10);

export default function ClientLoansTab({ client, onChanged }) {
    const [profile, setProfile] = useState(null);
    const [editingProfile, setEditingProfile] = useState(false);
    const [profileDraft, setProfileDraft] = useState({});
    const [loansData, setLoansData] = useState({ loans: [], totals: { all: {}, included: {} } });
    const [loanModal, setLoanModal] = useState(null); // {mode:'add'|'edit', data}
    const [results, setResults] = useState([]);
    const [resultModal, setResultModal] = useState(null);
    const [selected, setSelected] = useState(null);
    const [editingSelected, setEditingSelected] = useState(false);
    const [selectedDraft, setSelectedDraft] = useState({});
    const [assumptions, setAssumptions] = useState(null);
    const [qualifyConfirm, setQualifyConfirm] = useState(false);
    const [qualifying, setQualifying] = useState(false);
    const [readiness, setReadiness] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Phase 1 client-summary preview gate: require at least one calculator
    // result (selected or any qualification row) before opening the preview.
    const hasCalcResult = !!(selected?.selected_plan_name || (results && results.length));
    const openClientPreview = () => {
        if (!hasCalcResult) {
            toast.message("Run the calculator first to preview the client summary.");
            return;
        }
        setPreviewOpen(true);
    };

    const load = useCallback(async () => {
        try {
            const [p, l, r, s, a, rd] = await Promise.all([
                api.get(`/clients/${client.id}/qualification-profile`),
                api.get(`/clients/${client.id}/loans`),
                api.get(`/clients/${client.id}/qualification-results`),
                api.get(`/clients/${client.id}/selected-program`),
                api.get(`/clients/qualification/assumptions`),
                api.get(`/clients/${client.id}/qualification-readiness`),
            ]);
            setProfile(p.data || {}); setLoansData(l.data || { loans: [], totals: { all: {}, included: {} } });
            setResults(r.data || []); setSelected(s.data || {}); setAssumptions(a.data || null); setReadiness(rd.data || null);
        } catch (e) { console.error(e); }
    }, [client.id]);

    useEffect(() => { load(); }, [load]);

    if (!profile) return <div className="text-sm text-muted-foreground p-5">Loading…</div>;

    // ─── Profile section
    const startEditProfile = () => {
        setProfileDraft({
            dob: profile.dob || "",
            ssn_last4: profile.ssn_last4 || "",
            state: profile.state || "",
            taxable_income: profile.taxable_income || "unknown",
            idr_filing_status: profile.idr_filing_status || "unknown",
            annual_agi: profile.annual_agi ?? "",
            spouse_agi: profile.spouse_agi ?? "",
            spouse_debt: profile.spouse_debt ?? "",
            children_count: profile.children_count ?? 0,
            other_household_count: profile.other_household_count ?? 0,
            family_size_override: profile.family_size_override ?? "",
            pslf: profile.pslf || "unknown",
            total_permanent_disability: profile.total_permanent_disability || "unknown",
            borrower_defense: profile.borrower_defense || "unknown",
            service_type: profile.service_type || "needs_review",
            notes: profile.notes || "",
            // Phase 2B borrower-timing + plan-availability inputs
            tax_year_used: profile.tax_year_used || "",
            income_source: profile.income_source || "",
            first_borrowed_date: profile.first_borrowed_date || "",
            newest_loan_disbursement_date: profile.newest_loan_disbursement_date || "",
            has_parent_plus: !!profile.has_parent_plus,
            has_consolidated_parent_plus: !!profile.has_consolidated_parent_plus,
            ibr_variant: profile.ibr_variant || "auto",
        });
        setEditingProfile(true);
    };
    const saveProfile = async () => {
        try {
            // Last-4 only on the client side too — but server scrubs again.
            const last4 = (profileDraft.ssn_last4 || "").replace(/\D/g, "").slice(-4);
            const payload = {
                ...profileDraft,
                ssn_last4: last4 || null,
                annual_agi: num(profileDraft.annual_agi),
                spouse_agi: num(profileDraft.spouse_agi),
                spouse_debt: num(profileDraft.spouse_debt),
                children_count: int(profileDraft.children_count) ?? 0,
                other_household_count: int(profileDraft.other_household_count) ?? 0,
                family_size_override: int(profileDraft.family_size_override),
            };
            await api.put(`/clients/${client.id}/qualification-profile`, payload);
            toast.success("Qualification profile saved");
            setEditingProfile(false); load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // ─── Loans
    const submitLoan = async () => {
        const d = loanModal.data;
        const payload = {
            included: !!d.included, loan_type: d.loan_type, loan_date: d.loan_date || null,
            original_amount: num(d.original_amount), disbursed_amount: num(d.disbursed_amount),
            principal: num(d.principal), interest: num(d.interest),
            total_balance: num(d.total_balance),
            scheduled_monthly_payment: num(d.scheduled_monthly_payment),
            rate_type: d.rate_type, interest_rate: num(d.interest_rate),
            status: d.status, description: d.description, servicer: d.servicer,
        };
        try {
            if (loanModal.mode === "add") await api.post(`/clients/${client.id}/loans`, payload);
            else await api.patch(`/clients/${client.id}/loans/${d.id}`, payload);
            toast.success(loanModal.mode === "add" ? "Loan added" : "Loan updated");
            setLoanModal(null); load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteLoan = async (loan) => {
        if (!confirm(`Delete ${loan.loan_type || "loan"}?`)) return;
        try {
            await api.delete(`/clients/${client.id}/loans/${loan.id}`);
            toast.success("Loan deleted"); load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const toggleInclude = async (loan, included) => {
        try {
            await api.patch(`/clients/${client.id}/loans/${loan.id}`, { ...loan, included });
            load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // ─── Qualify: generate Phase 2A internal estimate rows
    const hasCalculatedRows = results.some(r => r.source === "calculated");
    const requestQualify = () => {
        if (hasCalculatedRows) setQualifyConfirm(true);
        else runQualify();
    };
    const runQualify = async () => {
        setQualifying(true);
        try {
            const r = await api.post(`/clients/${client.id}/qualify`);
            toast.success(`Internal estimate rows generated (${r.data.rows_generated}).`);
            setQualifyConfirm(false);
            load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setQualifying(false); }
    };

    // ─── Internal Summary print
    const openInternalSummary = async () => {
        try {
            const r = await api.get(`/clients/${client.id}/internal-summary`);
            renderInternalSummaryWindow(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // ─── Results
    const submitResult = async () => {
        const d = resultModal.data;
        const payload = {
            plan_name: d.plan_name, term_months: int(d.term_months), rate: num(d.rate),
            monthly_payment: num(d.monthly_payment), total_principal: num(d.total_principal),
            total_interest: num(d.total_interest), total_amount: num(d.total_amount),
            eligible_status: d.eligible_status, quoted_on: d.quoted_on || null,
            quoted_by: d.quoted_by, notes: d.notes,
        };
        try {
            if (resultModal.mode === "add") await api.post(`/clients/${client.id}/qualification-results`, payload);
            else await api.patch(`/clients/${client.id}/qualification-results/${d.id}`, payload);
            toast.success(resultModal.mode === "add" ? "Result added" : "Result updated");
            setResultModal(null); load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteResult = async (r) => {
        if (!confirm("Delete this manual result?")) return;
        try {
            await api.delete(`/clients/${client.id}/qualification-results/${r.id}`);
            toast.success("Result deleted"); load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const markSelected = async (r) => {
        try {
            await api.post(`/clients/${client.id}/qualification-results/${r.id}/mark-selected`);
            toast.success("Marked as selected program"); load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // ─── Selected program
    const startEditSelected = () => {
        setSelectedDraft({
            selected_plan_name: selected.selected_plan_name || "",
            rate: selected.rate ?? "", term_months: selected.term_months ?? "",
            monthly_payment: selected.monthly_payment ?? "",
            standard_payment: selected.standard_payment ?? "",
            quoted_on: selected.quoted_on || "", quoted_by: selected.quoted_by || "",
            notes: selected.notes || "",
        });
        setEditingSelected(true);
    };
    const saveSelected = async () => {
        try {
            await api.put(`/clients/${client.id}/selected-program`, {
                ...selectedDraft,
                rate: num(selectedDraft.rate), term_months: int(selectedDraft.term_months),
                monthly_payment: num(selectedDraft.monthly_payment),
                standard_payment: num(selectedDraft.standard_payment),
            });
            toast.success("Selected program saved");
            setEditingSelected(false); load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-4" data-testid="loans-tab">
            {/* Borrower Snapshot */}
            <Card title="Borrower Snapshot" subtitle="Read-only fields come from the client record. DOB / SSN Last 4 / State save here.">
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
                    <Cell label="A+ CaseFlow ID" value={client.acos_id} mono />
                    <Cell label="Skywave Case ID" value={client.case_id} mono />
                    <Cell label="Client Name" value={client.client_name} />
                    <Cell label="DOB" value={profile.dob} />
                    <Cell label="SSN (Last 4)" value={profile.ssn_last4 ? `••• ${profile.ssn_last4}` : null} mono />
                    <Cell label="State" value={profile.state} />
                    <Cell label="Primary Phone" value={client.phone_e164 || client.phone_raw} />
                    <Cell label="Email" value={client.email} />
                    <Cell label="Client Status" value={(client.client_status || "").replace("_", " ")} />
                    <Cell label="Program Type" value={client.program_type} />
                </dl>
            </Card>

            {/* Income / Household / IDR Profile */}
            <SectionCard title="Income / Household / IDR Profile" editing={editingProfile} onEdit={startEditProfile} onCancel={() => setEditingProfile(false)} onSave={saveProfile} testIdRoot="profile">
                {editingProfile ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Field label="DOB"><Input type="date" value={profileDraft.dob} onChange={e => setProfileDraft({ ...profileDraft, dob: e.target.value })} data-testid="profile-dob" /></Field>
                        <Field label="SSN (Last 4)"><Input maxLength={11} value={profileDraft.ssn_last4} onChange={e => setProfileDraft({ ...profileDraft, ssn_last4: e.target.value })} placeholder="Last 4 only" data-testid="profile-ssn4" /></Field>
                        <Field label="State"><Input value={profileDraft.state} onChange={e => setProfileDraft({ ...profileDraft, state: e.target.value })} data-testid="profile-state" /></Field>
                        <FieldSel label="Taxable Income" value={profileDraft.taxable_income} onChange={v => setProfileDraft({ ...profileDraft, taxable_income: v })} options={YNU} testId="profile-taxable" />
                        <FieldSel label="IDR Filing Status" value={profileDraft.idr_filing_status} onChange={v => setProfileDraft({ ...profileDraft, idr_filing_status: v })} options={FILING} testId="profile-filing" />
                        <FieldSel label="Service Type" value={profileDraft.service_type} onChange={v => setProfileDraft({ ...profileDraft, service_type: v })} options={SERVICE_TYPES} testId="profile-service-type" />
                        <Field label="Annual AGI ($)"><Input type="number" value={profileDraft.annual_agi} onChange={e => setProfileDraft({ ...profileDraft, annual_agi: e.target.value })} data-testid="profile-agi" /></Field>
                        <Field label="Spouse AGI ($)"><Input type="number" value={profileDraft.spouse_agi} onChange={e => setProfileDraft({ ...profileDraft, spouse_agi: e.target.value })} data-testid="profile-spouse-agi" /></Field>
                        <Field label="Spouse Debt ($)"><Input type="number" value={profileDraft.spouse_debt} onChange={e => setProfileDraft({ ...profileDraft, spouse_debt: e.target.value })} /></Field>
                        <Field label="Children"><Input type="number" value={profileDraft.children_count} onChange={e => setProfileDraft({ ...profileDraft, children_count: e.target.value })} data-testid="profile-children" /></Field>
                        <Field label="Other Household"><Input type="number" value={profileDraft.other_household_count} onChange={e => setProfileDraft({ ...profileDraft, other_household_count: e.target.value })} data-testid="profile-other" /></Field>
                        <Field label="Family Size Override"><Input type="number" placeholder="(leave blank to use calculated)" value={profileDraft.family_size_override} onChange={e => setProfileDraft({ ...profileDraft, family_size_override: e.target.value })} data-testid="profile-fs-override" /></Field>
                        <FieldSel label="PSLF" value={profileDraft.pslf} onChange={v => setProfileDraft({ ...profileDraft, pslf: v })} options={YNU} />
                        <FieldSel label="Total Permanent Disability" value={profileDraft.total_permanent_disability} onChange={v => setProfileDraft({ ...profileDraft, total_permanent_disability: v })} options={YNU} />
                        <FieldSel label="Borrower Defense" value={profileDraft.borrower_defense} onChange={v => setProfileDraft({ ...profileDraft, borrower_defense: v })} options={YNU} />
                        <Field label="First Borrowed Date"><Input type="date" value={profileDraft.first_borrowed_date} onChange={e => setProfileDraft({ ...profileDraft, first_borrowed_date: e.target.value })} data-testid="profile-first-borrowed" /></Field>
                        <Field label="Newest Loan Disbursement"><Input type="date" value={profileDraft.newest_loan_disbursement_date} onChange={e => setProfileDraft({ ...profileDraft, newest_loan_disbursement_date: e.target.value })} data-testid="profile-newest-disb" /></Field>
                        <FieldSel label="IBR Variant" value={profileDraft.ibr_variant} onChange={v => setProfileDraft({ ...profileDraft, ibr_variant: v })} options={IBR_VARIANTS} testId="profile-ibr-variant" />
                        <FieldSel label="Has Parent PLUS?" value={profileDraft.has_parent_plus ? "yes" : "no"} onChange={v => setProfileDraft({ ...profileDraft, has_parent_plus: v === "yes" })} options={[{ v: "no", l: "No" }, { v: "yes", l: "Yes" }]} />
                        <FieldSel label="Parent PLUS Consolidated?" value={profileDraft.has_consolidated_parent_plus ? "yes" : "no"} onChange={v => setProfileDraft({ ...profileDraft, has_consolidated_parent_plus: v === "yes" })} options={[{ v: "no", l: "No" }, { v: "yes", l: "Yes" }]} />
                        <Field label="Tax Year Used"><Input placeholder="e.g. 2024" value={profileDraft.tax_year_used} onChange={e => setProfileDraft({ ...profileDraft, tax_year_used: e.target.value })} /></Field>
                        <FieldSel label="Income Source" value={profileDraft.income_source || "unknown"} onChange={v => setProfileDraft({ ...profileDraft, income_source: v })} options={[{ v: "unknown", l: "—" }, { v: "tax_return", l: "Tax return" }, { v: "pay_stubs", l: "Pay stubs" }, { v: "other", l: "Other" }]} />
                        <div className="md:col-span-3 space-y-1">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Notes</div>
                            <Textarea value={profileDraft.notes} onChange={e => setProfileDraft({ ...profileDraft, notes: e.target.value })} rows={2} />
                        </div>
                    </div>
                ) : (
                    <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                        <Cell label="Filing Status" value={(FILING.find(f => f.v === profile.idr_filing_status) || {}).l} />
                        <Cell label="Service Type" value={(SERVICE_TYPES.find(s => s.v === profile.service_type) || {}).l} />
                        <Cell label="Taxable Income" value={profile.taxable_income} />
                        <Cell label="Annual AGI" value={fmt$(profile.annual_agi)} />
                        <Cell label="Spouse AGI" value={fmt$(profile.spouse_agi)} />
                        <Cell label="Spouse Debt" value={fmt$(profile.spouse_debt)} />
                        <Cell label="Children" value={profile.children_count} />
                        <Cell label="Other Household" value={profile.other_household_count} />
                        <Cell label="Family Size (calculated)" value={profile.family_size_calculated} />
                        <Cell label="Family Size (override)" value={profile.family_size_override ?? "—"} />
                        <Cell label="PSLF" value={profile.pslf} />
                        <Cell label="Total Permanent Disability" value={profile.total_permanent_disability} />
                        <Cell label="Borrower Defense" value={profile.borrower_defense} />
                        <Cell label="IBR Variant" value={profile.ibr_variant || "auto"} />
                        <Cell label="First Borrowed" value={profile.first_borrowed_date} />
                        <Cell label="Newest Disbursement" value={profile.newest_loan_disbursement_date} />
                        <Cell label="Tax Year" value={profile.tax_year_used} />
                        <Cell label="Has Parent PLUS" value={profile.has_parent_plus ? "Yes" : "No"} />
                        <Cell label="PP Consolidated" value={profile.has_consolidated_parent_plus ? "Yes" : "No"} />
                    </dl>
                )}
            </SectionCard>

            {/* Loan Table */}
            <div className="card-default p-5">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-sm font-medium text-gray-900">Loans</div>
                        <div className="text-xs text-muted-foreground">Toggle the Include column to control which loans roll into the totals.</div>
                    </div>
                    <Button size="sm" onClick={() => setLoanModal({ mode: "add", data: { included: true, loan_type: "Direct Unsubsidized", rate_type: "fixed", status: "active" } })} data-testid="add-loan-button">
                        <Plus className="h-4 w-4 mr-1" /> Add Loan
                    </Button>
                </div>
                {loansData.loans.length === 0 ? (
                    <EmptyState title="No loans added yet." help="Add the borrower's loans to begin qualification review." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                                <tr>
                                    <th className="py-2">Inc</th>
                                    <th className="py-2">Type</th>
                                    <th className="py-2">Date</th>
                                    <th className="py-2 text-right">Principal</th>
                                    <th className="py-2 text-right">Interest</th>
                                    <th className="py-2 text-right">Balance</th>
                                    <th className="py-2 text-right">Mo. Pmt</th>
                                    <th className="py-2 text-right">Rate</th>
                                    <th className="py-2">Status</th>
                                    <th className="py-2">Servicer</th>
                                    <th className="py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loansData.loans.map(ln => (
                                    <tr key={ln.id} className={`border-b border-border ${ln.included ? "" : "opacity-50"}`} data-testid={`loan-row-${ln.id}`}>
                                        <td className="py-2"><Checkbox checked={!!ln.included} onCheckedChange={(c) => toggleInclude(ln, !!c)} data-testid={`loan-include-${ln.id}`} /></td>
                                        <td className="py-2 text-xs">{ln.loan_type || "—"}</td>
                                        <td className="py-2 text-xs">{ln.loan_date || "—"}</td>
                                        <td className="py-2 text-xs text-right">{fmt$(ln.principal)}</td>
                                        <td className="py-2 text-xs text-right">{fmt$(ln.interest)}</td>
                                        <td className="py-2 text-xs text-right font-medium">{fmt$(ln.total_balance || (Number(ln.principal || 0) + Number(ln.interest || 0)))}</td>
                                        <td className="py-2 text-xs text-right">{fmt$(ln.scheduled_monthly_payment)}</td>
                                        <td className="py-2 text-xs text-right">{fmtPct(ln.interest_rate)}</td>
                                        <td className="py-2 text-xs capitalize">{ln.status}</td>
                                        <td className="py-2 text-xs">{ln.servicer || "—"}</td>
                                        <td className="py-2 text-right">
                                            <Button size="sm" variant="ghost" onClick={() => setLoanModal({ mode: "edit", data: { ...ln } })} data-testid={`edit-loan-${ln.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                                            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteLoan(ln)} data-testid={`delete-loan-${ln.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Loan totals — included loans (primary) vs all loans (muted) */}
                {loansData.loans.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 mt-2 border-t border-border" data-testid="loan-totals">
                        <Tile label="Included — Balance" v1={`${loansData.totals.included.count} included`} v2={fmt$(loansData.totals.included.total_balance)} highlight />
                        <Tile label="Included — Mo. Payment" v1="Sum across included loans" v2={fmt$(loansData.totals.included.scheduled_monthly_payment)} highlight />
                        <Tile label="All Loans — Balance" v1={`${loansData.totals.all.count} total`} v2={fmt$(loansData.totals.all.total_balance)} />
                        <Tile label="All Loans — Mo. Payment" v1="Sum across every loan" v2={fmt$(loansData.totals.all.scheduled_monthly_payment)} />
                    </div>
                )}
            </div>

            {/* Qualification Results */}
            <div className="card-default p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <div className="text-sm font-medium text-gray-900">Qualification Results</div>
                        <div className="text-xs text-muted-foreground">Internal estimates. Manual entries are preserved across Qualify runs.</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {readiness && (
                            <span className={`inline-flex items-center text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded border ${readiness.ready ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`} data-testid="readiness-chip">
                                {readiness.ready ? "Ready to Qualify" : "Missing Data"}
                            </span>
                        )}
                        <Button size="sm" variant="outline" onClick={openInternalSummary} data-testid="internal-summary-button"><Printer className="h-4 w-4 mr-1" /> Internal Summary</Button>
                        <Button size="sm" variant="outline" onClick={openClientPreview} data-testid="preview-client-summary-button"><Eye className="h-4 w-4 mr-1" /> Preview Client Summary</Button>
                        <Button size="sm" variant="outline" onClick={requestQualify} disabled={qualifying} data-testid="qualify-button"><Sparkles className="h-4 w-4 mr-1" /> {qualifying ? "Calculating…" : "Qualify"}</Button>
                        <Button size="sm" onClick={() => setResultModal({ mode: "add", data: { plan_name: "Income-Based Repayment", eligible_status: "manual" } })} data-testid="add-result-button"><Plus className="h-4 w-4 mr-1" /> Add Manual Result</Button>
                    </div>
                </div>

                {/* Readiness checklist — only if not ready */}
                {readiness && !readiness.ready && (
                    <div className="rounded-md border border-amber-200 bg-amber-50/60 text-amber-900 text-xs px-3 py-2 mb-3" data-testid="readiness-checklist">
                        <div className="font-semibold mb-1.5">Missing data — results may be marked Needs Review</div>
                        <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
                            {Object.entries(readiness.items).map(([k, v]) => (
                                <li key={k} className="inline-flex items-center gap-1.5">
                                    <span className={`inline-block h-2 w-2 rounded-full ${v ? "bg-emerald-500" : "bg-amber-500"}`} />
                                    <span className="capitalize">{k.replace(/_/g, " ")}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Internal-only disclaimer */}
                <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2 mb-3" data-testid="qual-disclaimer">
                    Internal estimate for review only. Verify eligibility and payment amounts with Federal Student Aid / servicer tools before quoting or submitting.
                </div>

                {/* Calculated Estimates */}
                <ResultSection
                    title="Calculated Estimates"
                    subtitle={assumptions ? `Version: ${assumptions.calculation_version}` : null}
                    rows={results.filter(r => r.source === "calculated")}
                    selectedId={selected?.source_result_id}
                    onSelect={markSelected}
                    onEdit={(r) => setResultModal({ mode: "edit", data: { ...r } })}
                    onDelete={deleteResult}
                    sourceLabel="Calculated"
                    emptyHelp="Click Qualify to generate Standard, Extended Standard, and IDR estimates."
                    testIdPrefix="calc"
                />

                {/* Manual Results */}
                <div className="mt-5">
                    <ResultSection
                        title="Manual Results"
                        subtitle="Added by reps for off-system scenarios."
                        rows={results.filter(r => r.source !== "calculated")}
                        selectedId={selected?.source_result_id}
                        onSelect={markSelected}
                        onEdit={(r) => setResultModal({ mode: "edit", data: { ...r } })}
                        onDelete={deleteResult}
                        sourceLabel="Manual"
                        emptyHelp="Use Add Manual Result for ad-hoc estimates."
                        testIdPrefix="manual"
                    />
                </div>
            </div>

            {/* Assumptions */}
            {assumptions && (
                <div className="card-default p-5" data-testid="assumptions-card">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div>
                            <div className="text-sm font-medium text-gray-900">Calculation Assumptions</div>
                            <div className="text-xs text-muted-foreground">{assumptions.ruleset_name}. Review calculator rules monthly against current FSA guidance.</div>
                        </div>
                        {assumptions.last_reviewed_date && (
                            <ReviewBadge last={assumptions.last_reviewed_date} />
                        )}
                    </div>
                    <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                        <Cell label="Version" value={assumptions.calculation_version} mono />
                        <Cell label="Ruleset" value={assumptions.ruleset_name} />
                        <Cell label="Last Reviewed" value={assumptions.last_reviewed_date} />
                        <Cell label="Poverty Year" value={assumptions.poverty_guideline_year} />
                        <Cell label="Default Rate" value={`${assumptions.default_interest_rate}%`} />
                        <Cell label="Discretionary × Poverty" value={`${assumptions.discretionary_income_multiplier}×`} />
                        <Cell label="Standard Term" value={`${assumptions.standard_term_months} mo`} />
                        <Cell label="Extended Term" value={`${assumptions.extended_term_months} mo`} />
                        <Cell label="RAP" value={assumptions.rap_enabled ? "Enabled" : "Disabled"} />
                        <div className="md:col-span-3 text-[11px] text-muted-foreground">{assumptions.poverty_guideline_source_note}</div>
                    </dl>
                </div>
            )}

            {/* Selected Program */}
            <SectionCard title="Selected Program Summary" editing={editingSelected} onEdit={startEditSelected} onCancel={() => setEditingSelected(false)} onSave={saveSelected} testIdRoot="selected" cardClass={selected?.selected_plan_name && !editingSelected ? "card-primary" : "card-default"}>
                {editingSelected ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <FieldSel label="Selected Plan" value={selectedDraft.selected_plan_name} onChange={v => setSelectedDraft({ ...selectedDraft, selected_plan_name: v })} options={PLAN_OPTIONS.map(p => ({ v: p, l: p }))} testId="selected-plan" />
                        <Field label="Rate (%)"><Input type="number" value={selectedDraft.rate} onChange={e => setSelectedDraft({ ...selectedDraft, rate: e.target.value })} /></Field>
                        <Field label="Term (months)"><Input type="number" value={selectedDraft.term_months} onChange={e => setSelectedDraft({ ...selectedDraft, term_months: e.target.value })} /></Field>
                        <Field label="Monthly Payment ($)"><Input type="number" value={selectedDraft.monthly_payment} onChange={e => setSelectedDraft({ ...selectedDraft, monthly_payment: e.target.value })} data-testid="selected-monthly" /></Field>
                        <Field label="Standard Payment ($)"><Input type="number" value={selectedDraft.standard_payment} onChange={e => setSelectedDraft({ ...selectedDraft, standard_payment: e.target.value })} /></Field>
                        <Field label="Quoted On"><Input type="date" value={selectedDraft.quoted_on} onChange={e => setSelectedDraft({ ...selectedDraft, quoted_on: e.target.value })} /></Field>
                        <Field label="Quoted By"><Input value={selectedDraft.quoted_by} onChange={e => setSelectedDraft({ ...selectedDraft, quoted_by: e.target.value })} /></Field>
                        <div className="md:col-span-3 space-y-1">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Notes</div>
                            <Textarea value={selectedDraft.notes} onChange={e => setSelectedDraft({ ...selectedDraft, notes: e.target.value })} rows={2} />
                        </div>
                    </div>
                ) : !selected?.selected_plan_name ? (
                    <EmptyState title="No selected program yet." help="Click Select on a result row, or use Edit to enter one manually." />
                ) : (
                    <div className="space-y-3" data-testid="selected-summary">
                        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                            <Cell label="Selected Plan" value={selected.selected_plan_name} />
                            <Cell label="Rate" value={fmtPct(selected.rate)} />
                            <Cell label="Term" value={selected.term_months ? `${selected.term_months} months` : "—"} />
                            <Cell label="Estimated Payment" value={fmt$(selected.monthly_payment)} />
                            <Cell label="Loans Included" value={selected.loans_included_count} />
                            <Cell label="Loan Amount Included" value={fmt$(selected.loan_amount_included)} />
                            <Cell label="Standard Payment" value={fmt$(selected.standard_payment)} />
                            <Cell label="Savings vs Standard" value={fmtSavings(selected.standard_payment, selected.monthly_payment)} />
                            <Cell label="Quoted On" value={selected.quoted_on} />
                            <Cell label="Quoted By" value={selected.quoted_by} />
                            <Cell label="Source" value={selected.source_type ? selected.source_type.replace("_", " ") : "manual"} />
                            <Cell label="Calc Version" value={selected.calculation_version || "—"} mono />
                            <Cell label="Last Reviewed" value={selected.assumptions_snapshot?.last_reviewed_date} />
                            {selected.notes && <div className="md:col-span-3 text-xs text-muted-foreground">{selected.notes}</div>}
                            <div className="md:col-span-3 text-[11px] text-muted-foreground italic">Internal estimate for review only. Verify with FSA before quoting.</div>
                        </dl>
                        {selected.source_result_id && !results.some(r => r.id === selected.source_result_id) && (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="selected-stale-source">
                                Selected program saved manually or source result no longer exists.
                            </div>
                        )}
                    </div>
                )}
            </SectionCard>

            {/* Loan modal */}
            <Dialog open={!!loanModal} onOpenChange={(o) => { if (!o) setLoanModal(null); }}>
                <DialogContent className="sm:max-w-[640px]" data-testid="loan-dialog">
                    <DialogHeader>
                        <DialogTitle>{loanModal?.mode === "add" ? "Add Loan" : "Edit Loan"}</DialogTitle>
                        <DialogDescription>All currency fields are optional. Balance auto-calculates from principal+interest if blank.</DialogDescription>
                    </DialogHeader>
                    {loanModal && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <FieldSel label="Loan Type" value={loanModal.data.loan_type} onChange={v => setLoanModal({ ...loanModal, data: { ...loanModal.data, loan_type: v } })} options={LOAN_TYPES.map(t => ({ v: t, l: t }))} testId="loan-type" />
                            <FieldSel label="Status" value={loanModal.data.status} onChange={v => setLoanModal({ ...loanModal, data: { ...loanModal.data, status: v } })} options={LOAN_STATUSES.map(s => ({ v: s, l: s }))} />
                            <Field label="Loan Date"><Input type="date" value={loanModal.data.loan_date || ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, loan_date: e.target.value } })} /></Field>
                            <Field label="Servicer"><Input value={loanModal.data.servicer || ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, servicer: e.target.value } })} data-testid="loan-servicer" /></Field>
                            <Field label="Original ($)"><Input type="number" value={loanModal.data.original_amount ?? ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, original_amount: e.target.value } })} /></Field>
                            <Field label="Disbursed ($)"><Input type="number" value={loanModal.data.disbursed_amount ?? ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, disbursed_amount: e.target.value } })} /></Field>
                            <Field label="Principal ($)"><Input type="number" value={loanModal.data.principal ?? ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, principal: e.target.value } })} data-testid="loan-principal" /></Field>
                            <Field label="Interest ($)"><Input type="number" value={loanModal.data.interest ?? ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, interest: e.target.value } })} data-testid="loan-interest" /></Field>
                            <Field label="Total Balance ($)"><Input type="number" placeholder="(auto = principal + interest)" value={loanModal.data.total_balance ?? ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, total_balance: e.target.value } })} /></Field>
                            <Field label="Scheduled Monthly ($)"><Input type="number" value={loanModal.data.scheduled_monthly_payment ?? ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, scheduled_monthly_payment: e.target.value } })} data-testid="loan-mo" /></Field>
                            <FieldSel label="Rate Type" value={loanModal.data.rate_type} onChange={v => setLoanModal({ ...loanModal, data: { ...loanModal.data, rate_type: v } })} options={RATE_TYPES.map(r => ({ v: r, l: r }))} />
                            <Field label="Interest Rate (%)"><Input type="number" step="0.01" value={loanModal.data.interest_rate ?? ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, interest_rate: e.target.value } })} data-testid="loan-rate" /></Field>
                            <label className="flex items-center gap-2 text-sm pt-6">
                                <Checkbox checked={!!loanModal.data.included} onCheckedChange={c => setLoanModal({ ...loanModal, data: { ...loanModal.data, included: !!c } })} data-testid="loan-included" />
                                <span>Include in totals</span>
                            </label>
                            <div className="md:col-span-2 space-y-1">
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Description</div>
                                <Textarea rows={2} value={loanModal.data.description || ""} onChange={e => setLoanModal({ ...loanModal, data: { ...loanModal.data, description: e.target.value } })} />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLoanModal(null)}>Cancel</Button>
                        <Button onClick={submitLoan} data-testid="loan-submit">{loanModal?.mode === "add" ? "Add" : "Save"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Result modal */}
            <Dialog open={!!resultModal} onOpenChange={(o) => { if (!o) setResultModal(null); }}>
                <DialogContent className="sm:max-w-[560px]" data-testid="result-dialog">
                    <DialogHeader>
                        <DialogTitle>{resultModal?.mode === "add" ? "Add Manual Result" : "Edit Manual Result"}</DialogTitle>
                        <DialogDescription>Manual estimates only — clearly labeled in the table.</DialogDescription>
                    </DialogHeader>
                    {resultModal && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <FieldSel label="Plan" value={resultModal.data.plan_name} onChange={v => setResultModal({ ...resultModal, data: { ...resultModal.data, plan_name: v } })} options={PLAN_OPTIONS.map(p => ({ v: p, l: p }))} testId="result-plan" />
                            <FieldSel label="Eligible Status" value={resultModal.data.eligible_status} onChange={v => setResultModal({ ...resultModal, data: { ...resultModal.data, eligible_status: v } })} options={ELIGIBLE} />
                            <Field label="Term (months)"><Input type="number" value={resultModal.data.term_months ?? ""} onChange={e => setResultModal({ ...resultModal, data: { ...resultModal.data, term_months: e.target.value } })} /></Field>
                            <Field label="Rate (%)"><Input type="number" step="0.01" value={resultModal.data.rate ?? ""} onChange={e => setResultModal({ ...resultModal, data: { ...resultModal.data, rate: e.target.value } })} /></Field>
                            <Field label="Monthly Payment ($)"><Input type="number" value={resultModal.data.monthly_payment ?? ""} onChange={e => setResultModal({ ...resultModal, data: { ...resultModal.data, monthly_payment: e.target.value } })} data-testid="result-monthly" /></Field>
                            <Field label="Total Principal ($)"><Input type="number" value={resultModal.data.total_principal ?? ""} onChange={e => setResultModal({ ...resultModal, data: { ...resultModal.data, total_principal: e.target.value } })} /></Field>
                            <Field label="Total Interest ($)"><Input type="number" value={resultModal.data.total_interest ?? ""} onChange={e => setResultModal({ ...resultModal, data: { ...resultModal.data, total_interest: e.target.value } })} /></Field>
                            <Field label="Total Amount ($)"><Input type="number" value={resultModal.data.total_amount ?? ""} onChange={e => setResultModal({ ...resultModal, data: { ...resultModal.data, total_amount: e.target.value } })} /></Field>
                            <Field label="Quoted On"><Input type="date" value={resultModal.data.quoted_on || ""} onChange={e => setResultModal({ ...resultModal, data: { ...resultModal.data, quoted_on: e.target.value } })} /></Field>
                            <Field label="Quoted By"><Input value={resultModal.data.quoted_by || ""} onChange={e => setResultModal({ ...resultModal, data: { ...resultModal.data, quoted_by: e.target.value } })} /></Field>
                            <div className="md:col-span-2 space-y-1">
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Notes</div>
                                <Textarea rows={2} value={resultModal.data.notes || ""} onChange={e => setResultModal({ ...resultModal, data: { ...resultModal.data, notes: e.target.value } })} />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResultModal(null)}>Cancel</Button>
                        <Button onClick={submitResult} data-testid="result-submit">{resultModal?.mode === "add" ? "Add" : "Save"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Qualify regenerate confirm */}
            <Dialog open={qualifyConfirm} onOpenChange={(o) => { if (!o) setQualifyConfirm(false); }}>
                <DialogContent className="sm:max-w-[460px]" data-testid="qualify-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>Regenerate calculated estimates?</DialogTitle>
                        <DialogDescription>
                            Existing calculated rows for this version will be replaced. Manual results are kept untouched.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setQualifyConfirm(false)}>Cancel</Button>
                        <Button onClick={runQualify} disabled={qualifying} data-testid="qualify-confirm-submit">
                            {qualifying ? "Calculating…" : "Regenerate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Client-facing summary preview (Phase 1) */}
            <ClientSummaryPreview
                open={previewOpen}
                onOpenChange={setPreviewOpen}
                client={client}
                profile={profile}
                loansData={loansData}
                results={results}
                selected={selected}
            />
        </div>
    );
}

function ResultSection({ title, subtitle, rows, selectedId, onSelect, onEdit, onDelete, sourceLabel, emptyHelp, testIdPrefix }) {
    return (
        <div data-testid={`${testIdPrefix}-section`}>
            <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">{title}</div>
                {subtitle && <div className="text-[10px] text-muted-foreground font-mono">{subtitle}</div>}
            </div>
            {rows.length === 0 ? (
                <div className="text-xs text-muted-foreground py-3 px-2 italic">{emptyHelp}</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                            <tr>
                                <th className="py-2">Plan</th>
                                <th className="py-2 text-right">Term</th>
                                <th className="py-2 text-right">Rate</th>
                                <th className="py-2 text-right">Monthly</th>
                                <th className="py-2 text-right">Total Principal</th>
                                <th className="py-2 text-right">Total Interest</th>
                                <th className="py-2 text-right">Total</th>
                                <th className="py-2">Status</th>
                                <th className="py-2">Source</th>
                                <th className="py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const isSel = selectedId && r.id === selectedId;
                                return (
                                    <tr key={r.id} className={`border-b border-border transition-colors ${isSel ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-gray-50/70"}`} data-testid={`result-row-${r.id}`}>
                                        <td className="py-2.5 text-xs">
                                            <div className="text-gray-900 flex items-center gap-2 font-medium">
                                                <span>{r.plan_name}</span>
                                                {isSel && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`selected-badge-${r.id}`}>
                                                        <CheckCircle2 className="h-3 w-3" /> Selected
                                                    </span>
                                                )}
                                            </div>
                                            {r.notes && <div className="text-[10px] text-muted-foreground mt-0.5 max-w-[280px] truncate" title={r.notes}>{r.notes}</div>}
                                        </td>
                                        <td className="py-2.5 text-xs text-right">{r.term_months != null ? `${r.term_months} mo` : "—"}</td>
                                        <td className="py-2.5 text-xs text-right">{r.rate != null ? `${Number(r.rate).toFixed(2)}%` : "—"}</td>
                                        <td className="py-2.5 text-xs text-right font-medium">{r.monthly_payment != null ? `$${Number(r.monthly_payment).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}</td>
                                        <td className="py-2.5 text-xs text-right">{r.total_principal != null ? `$${Number(r.total_principal).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}</td>
                                        <td className="py-2.5 text-xs text-right">{r.total_interest != null ? `$${Number(r.total_interest).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}</td>
                                        <td className="py-2.5 text-xs text-right">{r.total_amount != null ? `$${Number(r.total_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}</td>
                                        <td className="py-2.5 text-xs"><EligibleStatusPill status={r.eligible_status} /></td>
                                        <td className="py-2.5 text-xs"><ResultSourcePill source={r.source} /></td>
                                        <td className="py-2.5 text-right whitespace-nowrap">
                                            <Button size="sm" variant={isSel ? "default" : "outline"} onClick={() => onSelect(r)} className="mr-1" data-testid={`mark-selected-${r.id}`} disabled={isSel}>
                                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{isSel ? "Selected" : "Select"}
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => onEdit(r)} data-testid={`edit-result-${r.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                                            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onDelete(r)} data-testid={`delete-result-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Card({ title, subtitle, children }) {
    return (
        <div className="card-default p-5">
            <div className="mb-3">
                <div className="section-title flex items-center gap-2"><GitBranch className="h-4 w-4 text-primary" />{title}</div>
                {subtitle && <div className="section-subtitle">{subtitle}</div>}
            </div>
            {children}
        </div>
    );
}

function SectionCard({ title, editing, onEdit, onCancel, onSave, testIdRoot, children, cardClass = "card-default" }) {
    return (
        <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between mb-3">
                <div className="section-title">{title}</div>
                {onEdit && (editing ? (
                    <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={onCancel} data-testid={`${testIdRoot}-cancel`}><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>
                        <Button size="sm" onClick={onSave} data-testid={`${testIdRoot}-save`}>Save</Button>
                    </div>
                ) : (
                    <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`${testIdRoot}-edit`}><Edit3 className="h-3.5 w-3.5 mr-1" />Edit</Button>
                ))}
            </div>
            {children}
        </div>
    );
}

function Cell({ label, value, mono = false }) {
    return (
        <div className="flex flex-col">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-sm text-gray-800 ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block space-y-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            {children}
        </label>
    );
}
function FieldSel({ label, value, onChange, options, testId }) {
    // Defense-in-depth: Radix UI Select forbids empty-string item values.
    // If a caller passes one, swap it for "__none" so the form never crashes.
    const safeOptions = options.map(o => (o.v === "" ? { ...o, v: "__none" } : o));
    const safeValue = value === "" || value == null ? "__none" : value;
    const handle = (v) => onChange(v === "__none" ? "" : v);
    return (
        <label className="block space-y-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <Select value={safeValue} onValueChange={handle}>
                <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
                <SelectContent>{safeOptions.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
        </label>
    );
}
function Tile({ label, v1, v2, highlight = false }) {
    return (
        <div className={`rounded-lg p-3 border ${highlight ? "bg-primary/5 border-primary/30" : "bg-gray-50 border-border"}`}>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-xs text-gray-600">{v1}</div>
            <div className="text-lg font-semibold text-gray-900">{v2}</div>
        </div>
    );
}
function EmptyState({ title, help }) {    return (
        <div className="text-center py-8 text-muted-foreground">
            <div className="text-sm font-medium text-gray-700">{title}</div>
            <div className="text-xs mt-1">{help}</div>
        </div>
    );
}

function fmtSavings(std, sel) {
    if (std == null || sel == null) return "—";
    const diff = Number(std) - Number(sel);
    if (Math.abs(diff) < 0.5) return "$0/mo";
    const fmt = `$${Math.abs(diff).toLocaleString(undefined, { maximumFractionDigits: 2 })}/mo`;
    return diff > 0 ? `${fmt} lower` : `${fmt} higher`;
}

function ReviewBadge({ last }) {
    const daysAgo = Math.floor((Date.now() - new Date(last).getTime()) / 86400000);
    const overdue = daysAgo > 30;
    return (
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded border ${overdue ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid={overdue ? "review-overdue" : "review-fresh"} title={overdue ? "Calculator review due" : "Reviewed within last 30 days"}>
            Reviewed {daysAgo}d ago{overdue ? " · review due" : ""}
        </span>
    );
}

function renderInternalSummaryWindow(d) {
    const w = window.open("", "_blank", "noopener,width=900,height=1000");
    if (!w) { return; }
    const $ = (s) => String(s ?? "—");
    const fmt$ = (v) => v != null ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—";
    const escape = (s) => $(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" })[c]);
    const c = d.client || {}, p = d.profile || {}, sel = d.selected_program || {}, a = d.assumptions || {};
    const loansRows = (d.loans || []).map(l => `<tr><td>${escape(l.loan_type)}</td><td>${escape(l.servicer)}</td><td>${l.included ? "Yes" : "No"}</td><td style="text-align:right">${fmt$(l.principal)}</td><td style="text-align:right">${fmt$(l.total_balance)}</td><td style="text-align:right">${l.interest_rate != null ? Number(l.interest_rate).toFixed(2) + "%" : "—"}</td></tr>`).join("");
    const resRows = (d.results || []).map(r => `<tr><td>${escape(r.plan_name)}</td><td>${escape(r.source || "manual")}</td><td>${escape((r.eligible_status || "").replace(/_/g, " "))}</td><td style="text-align:right">${fmt$(r.monthly_payment)}</td><td style="text-align:right">${r.term_months ?? "—"} mo</td><td style="text-align:right">${r.rate != null ? Number(r.rate).toFixed(2) + "%" : "—"}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Internal Loans & Qualification Summary</title>
    <style>
    body{font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Inter,sans-serif;color:#111827;margin:32px;max-width:880px}
    h1{font-size:20px;margin:0 0 4px}
    h2{font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin:24px 0 8px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
    .meta{font-size:12px;color:#6b7280}
    .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:6px 18px;font-size:13px}
    .lbl{font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em}
    .val{font-weight:500}
    table{width:100%;border-collapse:collapse;font-size:12px;margin-top:6px}
    th{text-align:left;text-transform:uppercase;font-size:10px;letter-spacing:0.06em;color:#6b7280;border-bottom:1px solid #d1d5db;padding:6px 6px}
    td{border-bottom:1px solid #f3f4f6;padding:6px 6px}
    .disc{margin-top:24px;padding:10px 12px;border:1px solid #fcd34d;background:#fffbeb;color:#7c2d12;font-size:12px;border-radius:6px}
    @media print { .noprint { display: none } body { margin: 0.5in } }
    .noprint{margin-top:14px}
    button{padding:6px 12px;font-size:12px;border:1px solid #111827;background:#111827;color:#fff;border-radius:6px;cursor:pointer}
    button.secondary{background:#fff;color:#111827}
    </style></head><body>
    <h1>Internal Loans & Qualification Summary</h1>
    <div class="meta">Generated ${escape(d.generated_at)} · Calc version ${escape(a.calculation_version)} · Last reviewed ${escape(a.last_reviewed_date)}</div>

    <h2>Client</h2>
    <div class="grid">
      <div><div class="lbl">A+ CaseFlow ID</div><div class="val">${escape(c.acos_id)}</div></div>
      <div><div class="lbl">Skywave Case ID</div><div class="val">${escape(c.case_id)}</div></div>
      <div><div class="lbl">Name</div><div class="val">${escape(c.client_name)}</div></div>
      <div><div class="lbl">Phone</div><div class="val">${escape(c.phone_e164)}</div></div>
      <div><div class="lbl">Email</div><div class="val">${escape(c.email)}</div></div>
      <div><div class="lbl">Status / Program</div><div class="val">${escape(c.client_status)} · ${escape(c.program_type)}</div></div>
    </div>

    <h2>Borrower Profile</h2>
    <div class="grid">
      <div><div class="lbl">DOB</div><div class="val">${escape(p.dob)}</div></div>
      <div><div class="lbl">SSN (Last 4)</div><div class="val">${p.ssn_last4 ? "••• " + escape(p.ssn_last4) : "—"}</div></div>
      <div><div class="lbl">State</div><div class="val">${escape(p.state)}</div></div>
      <div><div class="lbl">Filing Status</div><div class="val">${escape((p.idr_filing_status || "").replace(/_/g, " "))}</div></div>
      <div><div class="lbl">Annual AGI</div><div class="val">${fmt$(p.annual_agi)}</div></div>
      <div><div class="lbl">Spouse AGI</div><div class="val">${fmt$(p.spouse_agi)}</div></div>
      <div><div class="lbl">Family Size</div><div class="val">${escape(p.family_size_override ?? p.family_size_calculated)}</div></div>
      <div><div class="lbl">Service Type</div><div class="val">${escape((p.service_type || "").replace(/_/g, " "))}</div></div>
      <div><div class="lbl">IBR Variant</div><div class="val">${escape(p.ibr_variant || "auto")}</div></div>
    </div>

    <h2>Included Loans</h2>
    <table><thead><tr><th>Type</th><th>Servicer</th><th>Inc</th><th style="text-align:right">Principal</th><th style="text-align:right">Balance</th><th style="text-align:right">Rate</th></tr></thead><tbody>${loansRows || `<tr><td colspan="6" style="color:#6b7280;font-style:italic;text-align:center">No loans entered</td></tr>`}</tbody></table>
    <div class="meta" style="margin-top:6px">Total included balance: <strong>${fmt$(d.totals?.included?.total_balance)}</strong> · ${escape(d.totals?.included?.count ?? 0)} loan(s)</div>

    <h2>Qualification Results</h2>
    <table><thead><tr><th>Plan</th><th>Source</th><th>Status</th><th style="text-align:right">Monthly</th><th style="text-align:right">Term</th><th style="text-align:right">Rate</th></tr></thead><tbody>${resRows || `<tr><td colspan="6" style="color:#6b7280;font-style:italic;text-align:center">No results yet</td></tr>`}</tbody></table>

    <h2>Selected Program</h2>
    ${sel.selected_plan_name ? `<div class="grid">
      <div><div class="lbl">Plan</div><div class="val">${escape(sel.selected_plan_name)}</div></div>
      <div><div class="lbl">Monthly Payment</div><div class="val">${fmt$(sel.monthly_payment)}</div></div>
      <div><div class="lbl">Standard Payment</div><div class="val">${fmt$(sel.standard_payment)}</div></div>
      <div><div class="lbl">Loans Included</div><div class="val">${escape(sel.loans_included_count)} · ${fmt$(sel.loan_amount_included)}</div></div>
      <div><div class="lbl">Term</div><div class="val">${escape(sel.term_months ? sel.term_months + " mo" : "—")}</div></div>
      <div><div class="lbl">Rate</div><div class="val">${sel.rate != null ? Number(sel.rate).toFixed(2) + "%" : "—"}</div></div>
      <div><div class="lbl">Quoted On / By</div><div class="val">${escape(sel.quoted_on)} · ${escape(sel.quoted_by)}</div></div>
    </div>` : `<div class="meta" style="font-style:italic">No selected program.</div>`}

    <h2>Calculation Assumptions</h2>
    <div class="grid">
      <div><div class="lbl">Version</div><div class="val">${escape(a.calculation_version)}</div></div>
      <div><div class="lbl">Ruleset</div><div class="val">${escape(a.ruleset_name)}</div></div>
      <div><div class="lbl">Last Reviewed</div><div class="val">${escape(a.last_reviewed_date)}</div></div>
      <div><div class="lbl">Default Rate</div><div class="val">${escape(a.default_interest_rate)}%</div></div>
      <div><div class="lbl">Discretionary ×</div><div class="val">${escape(a.discretionary_income_multiplier)}× poverty</div></div>
      <div><div class="lbl">Standard Term</div><div class="val">${escape(a.standard_term_months)} mo</div></div>
    </div>

    <div class="disc"><strong>Internal Estimate.</strong> ${escape(d.disclaimer)}</div>
    <div class="noprint">
      <button onclick="window.print()">Print</button>
      <button class="secondary" onclick="window.close()">Close</button>
    </div>
    </body></html>`);
    w.document.close();
}
